import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import dayjs from 'dayjs';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { UpcomingReminderDashboardDto } from 'api/dashboard/dtos/UpcomingReminderDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { DATE_FORMAT_DEFAUT } from 'Config';
import useInfiniteReveal from '../components/useInfiniteReveal';
import upcomingStyles from '../upcomingEvents/UpcomingEvents.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './UpcomingReminders.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

type Props = {
	data?: UpcomingReminderDashboardDto[] | null;
	isLoading?: boolean;
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const UpcomingReminders = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<UpcomingReminderDashboardDto[] | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);

	const isLegacyMode = props.data !== undefined;
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { ...vehicleScope };
				const result = await DashboardService.getUpcomingReminders(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setFetchLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode]);

	const data = isLegacyMode ? props.data ?? [] : fetchedData ?? [];
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const { visibleItems: pageItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	const formatCriteria = (item: UpcomingReminderDashboardDto) => {
		if (item.criteria === 'DATE' && item.nextDate) return dayjs(item.nextDate).format(DATE_FORMAT_DEFAUT);
		if (item.criteria === 'KMS' && item.nextKm != null) return `${item.nextKm.toLocaleString()} km`;
		return '-';
	};

	const repeatLabel = (value: number, interval: string) => {
		return `${value} ` + t(`reminders.list.${interval}${value > 1 ? '_PLURAL' : '_SINGLE'}` as any);
	};

	const formatAlertBefore = (item: UpcomingReminderDashboardDto) => {
		if (item.criteria === 'DATE' && item.beforeDateValue && item.beforeDateMeasure) return repeatLabel(item.beforeDateValue, item.beforeDateMeasure);
		if (item.criteria === 'KMS' && item.beforeKm) return repeatLabel(item.beforeKm, 'KMS');
		if (item.criteria === 'HOURS' && item.beforeHour) return repeatLabel(item.beforeHour, 'HOURS');
		return '-';
	};

	const formatStop = (item: UpcomingReminderDashboardDto) => {
		if (item.criteria === 'DATE' && item.stopDate) return dayjs(item.stopDate).format(DATE_FORMAT_DEFAUT);
		if (item.criteria === 'KMS' && item.stopKm != null) return `${item.stopKm.toLocaleString()} km`;
		return '—';
	};

	const buildVehicleTooltip = (item: UpcomingReminderDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	const formatCategory = (item: UpcomingReminderDashboardDto) => {
		if (item.vehicleMaintenanceTypeName) {
			return item.vehicleMaintenanceTypeIsSystem
				? t(('common.maintenance_contract_type.' + item.vehicleMaintenanceTypeName) as any) as string
				: item.vehicleMaintenanceTypeName;
		}
		return item.categoryIsSystem
			? t(`reminder_categories.category_types.${item.categoryDesignation}` as any) as string
			: item.categoryDesignation;
	};

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.upcoming_reminders') as string}
			subTitleTooltip={t('dashboard.upcoming_reminders_tooltip' as any) as string}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<div className={upcomingStyles.tableWrapper}>
						<table className={upcomingStyles.table}>
							<thead>
								<tr>
									<th>{t('dashboard.upcoming_reminders_cols.asset')}</th>
									<th>{t('dashboard.upcoming_reminders_cols.category')}</th>
									<th>{t('dashboard.upcoming_reminders_cols.criteria')}</th>
									<th>{t('dashboard.upcoming_reminders_cols.alert_before')}</th>
									<th>{t('dashboard.upcoming_reminders_cols.valid_until')}</th>
									<th>{t('dashboard.upcoming_reminders_cols.status')}</th>
								</tr>
							</thead>
							<tbody>
								{pageItems.map(item => (
									<tr key={item.triggerId}>
										<td>
											<span
												className={styles.plate}
												data-tip={buildVehicleTooltip(item)}
												data-for="upcoming-reminders-tooltip"
												data-place="right"
											>
												{item.registrationNumber}
											</span>
										</td>
										<td>{formatCategory(item)}</td>
										<td>{formatCriteria(item)}</td>
										<td>{formatAlertBefore(item)}</td>
										<td>{formatStop(item)}</td>
										<td>
											<span className={`${styles.statusBadge} ${styles.statusActive}`}>
												{t('reminders.notifications.status.ACTIVE' as any)}
											</span>
										</td>
									</tr>
								))}
								{hasMore && (
									<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
										<td colSpan={6} className={infiniteStyles.sentinelRow} />
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<ReactTooltip id="upcoming-reminders-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default UpcomingReminders;
